import { usePage } from "@inertiajs/react"
import { useEffect, useRef, useState } from "react"

const DISMISS_AFTER_MS = 2000

export default function Flash() {
  const { flash } = usePage()
  // Each visit brings a new flash object, so the same message shown twice in a row still remounts and restarts its timer.
  const visit = useRef({ flash, id: 0 })
  if (visit.current.flash !== flash) visit.current = { flash, id: visit.current.id + 1 }

  return (
    <>
      {flash.notice && <Notice key={visit.current.id} message={flash.notice} />}
      {flash.alert && (
        <p role="alert" className="mb-4 rounded-surface bg-red-50 px-4 py-3 text-red-900 dark:bg-red-950 dark:text-red-100">
          {flash.alert}
        </p>
      )}
    </>
  )
}

// Floats instead of sitting in the page so nothing jumps when it goes. Hovering or focusing it holds it open.
export function Notice({ message }: { message: string }) {
  const [shown, setShown] = useState(true)
  const [held, setHeld] = useState(false)

  useEffect(() => {
    if (held || !shown) return
    const timer = setTimeout(() => setShown(false), DISMISS_AFTER_MS)
    return () => clearTimeout(timer)
  }, [held, shown])

  if (!shown) return null

  return (
    <div
      role="status"
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)}
      onBlur={() => setHeld(false)}
      className="fixed top-20 right-4 left-4 z-30 mx-auto flex max-w-sm items-center gap-3 rounded-surface bg-green-50 py-2 pr-2 pl-4 text-green-900 shadow-[0_16px_40px_-12px_rgb(24_23_29/0.35)] motion-safe:animate-[toast-in_200ms_ease-out] sm:left-auto dark:bg-green-950 dark:text-green-100"
    >
      <p className="min-w-0 flex-1">{message}</p>
      <button
        type="button"
        onClick={() => setShown(false)}
        className="flex size-8 shrink-0 items-center justify-center rounded-control hover:bg-green-100 dark:hover:bg-green-900"
      >
        <span aria-hidden="true">✕</span>
        <span className="sr-only">Dismiss</span>
      </button>
    </div>
  )
}
